import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  SignupApproval,
  SignupDecisionResult,
  SignupRejection,
  TenantSignup,
  TenantSignupListResponse,
  LifecycleEvent,
  LifecycleTransitionRequest,
  InstallationSummary,
  Organisation,
  OrganisationArchive,
  OrganisationCreate,
  OrganisationListResponse,
  OrganisationUpdate,
  OverviewResponse,
  AuditListResponse,
} from '@hospasense/contracts';
import { apiGet, apiSend } from './api.js';

export function useOverview() {
  return useQuery<OverviewResponse>({
    queryKey: ['overview'],
    queryFn: () => apiGet('/api/v1/overview'),
    staleTime: 30_000,
  });
}

export function useOrganisations(params: URLSearchParams) {
  const qs = params.toString();
  return useQuery<OrganisationListResponse>({
    queryKey: ['organisations', qs],
    queryFn: () => apiGet(`/api/v1/organisations${qs ? `?${qs}` : ''}`),
    placeholderData: (prev) => prev,
  });
}

export function useOrganisation(id: string | undefined) {
  return useQuery<Organisation>({
    queryKey: ['organisation', id],
    queryFn: () => apiGet(`/api/v1/organisations/${id}`),
    enabled: Boolean(id),
  });
}

export function useLifecycleEvents(id: string | undefined) {
  return useQuery<{ items: LifecycleEvent[] }>({
    queryKey: ['organisation', id, 'lifecycle'],
    queryFn: () => apiGet(`/api/v1/organisations/${id}/lifecycle-events`),
    enabled: Boolean(id),
  });
}

export function useInstallations(id: string | undefined, enabled: boolean) {
  return useQuery<{ items: InstallationSummary[] }>({
    queryKey: ['organisation', id, 'installations'],
    queryFn: () => apiGet(`/api/v1/organisations/${id}/installations`),
    enabled: Boolean(id) && enabled,
  });
}

export function useOperators(enabled: boolean) {
  return useQuery<{ items: { id: string; displayName: string }[] }>({
    queryKey: ['operators'],
    queryFn: () => apiGet('/api/v1/meta/operators'),
    enabled,
    staleTime: 300_000,
  });
}

export function useAuditEvents(params: URLSearchParams, enabled: boolean) {
  const qs = params.toString();
  return useQuery<AuditListResponse>({
    queryKey: ['audit', qs],
    queryFn: () => apiGet(`/api/v1/audit-events${qs ? `?${qs}` : ''}`),
    enabled,
    placeholderData: (prev) => prev,
  });
}

function useInvalidateOrg() {
  const qc = useQueryClient();
  return async (id?: string): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['organisations'] });
    await qc.invalidateQueries({ queryKey: ['overview'] });
    if (id) await qc.invalidateQueries({ queryKey: ['organisation', id] });
  };
}

export interface OrganisationModuleState {
  key: string;
  label: string;
  group: string;
  enabled: boolean | null;
  confirmedAt: string | null;
  pendingAction: 'enable_module' | 'disable_module' | null;
  pendingCommandId: string | null;
}

export interface OrganisationModulesResponse {
  organisationId: string;
  organisationName: string;
  installationId: string | null;
  modules: OrganisationModuleState[];
}

export function useOrganisationModules(orgId: string | undefined) {
  return useQuery<OrganisationModulesResponse>({
    queryKey: ['organisation-modules', orgId],
    queryFn: () => apiGet(`/api/v1/organisations/${orgId}/modules`),
    enabled: Boolean(orgId),
  });
}

export function useSetOrganisationModules(orgId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { desired: Record<string, boolean>; reason: string }) =>
      apiSend<{
        queued: Array<{ key: string; action: string; commandId: string }>;
        unchanged: string[];
      }>('POST', `/api/v1/organisations/${orgId}/modules`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['organisation-modules', orgId] });
      void qc.invalidateQueries({ queryKey: ['provisioning'] });
    },
  });
}

export function useCreateOrganisation() {
  const invalidate = useInvalidateOrg();
  return useMutation({
    mutationFn: (input: OrganisationCreate) =>
      apiSend<Organisation>('POST', '/api/v1/organisations', input),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateOrganisation(id: string) {
  const invalidate = useInvalidateOrg();
  return useMutation({
    mutationFn: (input: OrganisationUpdate) =>
      apiSend<Organisation>('PATCH', `/api/v1/organisations/${id}`, input),
    onSuccess: () => invalidate(id),
  });
}

export function useTransitionLifecycle(id: string) {
  const invalidate = useInvalidateOrg();
  return useMutation({
    mutationFn: (input: LifecycleTransitionRequest) =>
      apiSend<Organisation>('POST', `/api/v1/organisations/${id}/lifecycle`, input),
    onSuccess: () => invalidate(id),
  });
}

export function useArchiveOrganisation(id: string) {
  const invalidate = useInvalidateOrg();
  return useMutation({
    mutationFn: (input: OrganisationArchive) =>
      apiSend<Organisation>('POST', `/api/v1/organisations/${id}/archive`, input),
    onSuccess: () => invalidate(id),
  });
}

/**
 * Pending hospital signups.
 *
 * `refetchInterval` is set because this is a QUEUE, not a report: an operator
 * leaves it open and a hospital registering should appear without a reload.
 * Thirty seconds rather than a socket — the event volume is a few a week, and a
 * polling interval that costs one cheap query is not worth a new transport.
 */
export function useSignups(params: URLSearchParams) {
  const qs = params.toString();
  return useQuery<TenantSignupListResponse>({
    queryKey: ['signups', qs],
    queryFn: () => apiGet(`/api/v1/signups${qs ? `?${qs}` : ''}`),
    placeholderData: (prev) => prev,
    refetchInterval: 30_000,
  });
}

export function useSignup(id: string | undefined) {
  return useQuery<TenantSignup>({
    queryKey: ['signup', id],
    queryFn: () => apiGet(`/api/v1/signups/${id}`),
    enabled: Boolean(id),
  });
}

function useInvalidateSignups() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['signups'] });
    void qc.invalidateQueries({ queryKey: ['signup'] });
    /**
     * Approving creates an ORGANISATION, so the customer list is stale too.
     * Forgetting this is how an operator approves a hospital, clicks through to
     * Organisations, and does not find it — then reasonably concludes the
     * approval silently failed.
     */
    void qc.invalidateQueries({ queryKey: ['organisations'] });
    void qc.invalidateQueries({ queryKey: ['overview'] });
  };
}

export function useApproveSignup(id: string) {
  const invalidate = useInvalidateSignups();
  return useMutation({
    mutationFn: (input: SignupApproval) =>
      apiSend<SignupDecisionResult>('POST', `/api/v1/signups/${id}/approve`, input),
    onSuccess: () => invalidate(),
  });
}

export function useRejectSignup(id: string) {
  const invalidate = useInvalidateSignups();
  return useMutation({
    mutationFn: (input: SignupRejection) =>
      apiSend<SignupDecisionResult>('POST', `/api/v1/signups/${id}/reject`, input),
    onSuccess: () => invalidate(),
  });
}
